import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../LanguageContext';

/**
 * Countries and tags used to sit under the bio as two rows of chips that grew
 * with the list — ten tags pushed the links off the first screen on a phone.
 * They are now two icon buttons directly under the name: grey until touched,
 * each opening the list it holds.
 *
 * Shared by `pages/Profile.jsx` (own profile) and `pages/User.jsx` (public
 * profile) so the two never drift apart. The public payload stores these as
 * `{name}` objects and the own-profile one as plain strings, hence `labelOf`.
 */

const labelOf = (item) => (typeof item === 'string' ? item : (item?.name ?? ''));

const GlobeIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.25 10a8.75 8.75 0 0117.5 0v1a8.75 8.75 0 01-17.5 0v-1z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 22a9.962 9.962 0 005.656-1.722" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const TagIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
);

/* Colour lives here rather than inline so the grey→brand transition is the
   only difference between the two buttons. */
const TONES = {
    country: {
        idle: 'border-slate-200 bg-slate-50 text-slate-400 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600',
        open: 'border-sky-200 bg-sky-50 text-sky-600',
        chip: 'bg-sky-50 text-sky-700 border-sky-100',
    },
    tag: {
        idle: 'border-slate-200 bg-slate-50 text-slate-400 hover:border-primary-200 hover:bg-primary-50 hover:text-primary-600',
        open: 'border-primary-200 bg-primary-50 text-primary-600',
        chip: 'bg-primary-50 text-primary-700 border-primary-100',
    },
};

function Facet({ kind, Icon, title, items, open, onToggle, themed }) {
    const tone = TONES[kind];
    // On a themed public profile the resting state comes from the theme's own
    // soft surface, so these two pills cannot end up as the only pale objects
    // on a dark card. The open state keeps its brand tint — it is the one
    // moment the control is meant to stand out. The popover stays white on
    // every theme: it floats above the page rather than sitting on it.
    const idle = themed ? 'pf-soft pf-muted' : tone.idle;

    return (
        <div className="relative">
            <button
                type="button"
                onClick={onToggle}
                title={title}
                aria-label={title}
                aria-haspopup="true"
                aria-expanded={open}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition
                    ${open ? tone.open : idle}`}
            >
                <Icon className="w-4 h-4" />
                <span className="tabular-nums">{items.length}</span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute left-1/2 top-full z-30 mt-2 w-max min-w-[11rem] max-w-[15rem] -translate-x-1/2
                        rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-xl"
                >
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
                    <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
                        {items.map((item, i) => (
                            <span
                                key={i}
                                className={`rounded-full border px-2.5 py-1 text-xs font-medium ${tone.chip}`}
                            >
                                {labelOf(item)}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ProfileFacets({ countries = [], tags = [], className = '', themed = false }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(null);   // 'country' | 'tag' | null
    const ref = useRef(null);

    /* One listener for both menus: a second one opening closes the first, and
       a press anywhere else closes whichever is up. */
    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(null);
        };
        const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(null); };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const countryList = (countries || []).filter((c) => labelOf(c));
    const tagList = (tags || []).filter((tg) => labelOf(tg));

    if (countryList.length === 0 && tagList.length === 0) return null;

    return (
        <div ref={ref} className={`flex items-center justify-center gap-2 ${className}`}>
            {countryList.length > 0 && (
                <Facet
                    kind="country"
                    Icon={GlobeIcon}
                    title={t('profile.locationsLabel')}
                    items={countryList}
                    open={open === 'country'}
                    themed={themed}
                    onToggle={() => setOpen(open === 'country' ? null : 'country')}
                />
            )}
            {tagList.length > 0 && (
                <Facet
                    kind="tag"
                    Icon={TagIcon}
                    title={t('profile.tagsLabel')}
                    items={tagList}
                    open={open === 'tag'}
                    themed={themed}
                    onToggle={() => setOpen(open === 'tag' ? null : 'tag')}
                />
            )}
        </div>
    );
}
